
import { getLog } from "./helpers/helpers"

const idbKeyval = require("idb-keyval")


// -----------------------------------------------------------------------
//
//            Bsv_History_Cache (IndexedDB-backed)
//
//  Stores the per-tx enriched BSV transaction data produced by
//  processOneBsvTxData() (each entry costs 1 WhatsOnChain fetchTxInfo
//  call). Keyed by tx_hash as a hashmap for O(1) lookup.
//
//  Backed by idb-keyval, one IndexedDB database per (network + purse),
//  so mainnet/testnet and different wallets are isolated. BSV history is
//  fetched per purse address (wocConnector.fetchAddressHistory(purse)),
//  so the cache is keyed by purse — NOT by owner.
//
//  Survives page reloads and browser restarts (IndexedDB is persistent).
//
// -----------------------------------------------------------------------


export default class BsvHistoryCache {

    constructor(network, purse_address) {
        this.classname = "BsvHistoryCache: "
        const function_id = this.classname + "constructor(): "
        const log = getLog(function_id)

        this.cachekey = "bsv_" + network + "_" + purse_address
        log("cachekey=", this.cachekey)

        // kept as separate fields so callers can derive a matching localStorage
        // key without relying on React's async this.state flush (which lags
        // behind setStateSafe() right after wallet unlock).
        this.network = network
        this.purse = purse_address

        this.cache = {}      // tx_hash -> enriched entry (in-memory mirror)
        this.size = 0

        // idbKeyval: createStore() won't let us create multiple stores within
        // the same database, so each (network, purse) gets its own db.
        this.db_name = this.cachekey
        this.store_name = "keyval"
        log("idbKeyval.createStore(): ", this.db_name, this.store_name)
        this.customStore = idbKeyval.createStore(this.db_name, this.store_name)
    }

    async init() {
        const function_id = this.classname + "init(): "
        const log = getLog(function_id)
        log("start")

        try {
            const entries_array = await idbKeyval.entries(this.customStore)
            log("entries length=", entries_array.length)
            for (let i = 0; i < entries_array.length; i++) {
                const entry = entries_array[i]
                this.cache[entry[0]] = entry[1]
            }
            this.size = Object.keys(this.cache).length
            log("this.size=", this.size)
        } catch (error) {
            throw new Error(function_id + error)
        }
    }

    /**
     * @param {string} txhash
     * @returns true or false
     */
    isTXhashInCache(txhash) {
        return Object.prototype.hasOwnProperty.call(this.cache, txhash)
    }

    /**
     * @param {string} txhash
     * @returns the cached enriched entry or null
     */
    get_entry(txhash) {
        return this.cache[txhash] || null
    }

    /**
     * @returns the cache as an array of enriched entries
     */
    get_all() {
        return Object.values(this.cache)
    }

    /**
     * Add a single enriched entry, keyed by its tx_hash.
     * Write-through: mirrors to in-memory cache + IndexedDB.
     * Idempotent: re-adding an existing tx_hash is a no-op.
     *
     * @param {object} entry enriched txdata from processOneTxData (must have tx_hash)
     */
    async add_entry(entry) {
        const function_id = this.classname + "add_entry(): "
        const log = getLog(function_id)

        if (!entry || !entry.tx_hash) {
            log("ignoring entry without tx_hash", entry)
            return
        }
        if (this.cache[entry.tx_hash]) {
            return  // already cached
        }
        this.cache[entry.tx_hash] = entry
        this.size++
        try {
            await idbKeyval.set(entry.tx_hash, entry, this.customStore)
        } catch (error) {
            log("idbKeyval.set failed for tx", entry.tx_hash, error)
        }
    }

    /**
     * Clear the cache (in-memory + IndexedDB).
     */
    async clear() {
        const function_id = this.classname + "clear(): "
        const log = getLog(function_id)
        log("clearing", this.size, "entries")
        this.cache = {}
        this.size = 0
        try {
            await idbKeyval.clear(this.customStore)
        } catch (error) {
            log("idbKeyval.clear failed", error)
        }
    }
}
